import { Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import Back from '@mui/icons-material/KeyboardBackspaceRounded';
import {
  OnboardingModalCategory,
  OnboardingModalStep,
} from '../../../types/businesses';
import {
  ONBOARDING_CATEGORY_NAMES,
  ONBOARDING_SETUP_STEPS,
} from '../../../constants/businesses';
import SmallModal from '../../global/SmallModal';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import Progress from '../../global/ProgressBar';
import StepRow from './StepRow';

interface Props {
  category: OnboardingModalCategory;
  setCategory: (category: OnboardingModalCategory) => void;
  canGoBack?: boolean;
}

function OnboardingCategoryModal({ category, setCategory, canGoBack }: Props) {
  const theme = useTheme();
  const {
    isEnabled,
    categorySteps,
    categoryPercentCompleted,
    percentCompleted,
  } = useGetOnboarding();

  if (!isEnabled || !category) return null;

  const steps: OnboardingModalStep[] =
    category === 'SETUP'
      ? ONBOARDING_SETUP_STEPS
      : categorySteps?.[category] || [];
  const progress =
    category === 'SETUP'
      ? percentCompleted
      : categoryPercentCompleted[category];

  const onClose = () => {
    if (canGoBack) {
      setCategory('SETUP');
    } else {
      setCategory(null);
    }
  };

  const CloseIcon = canGoBack ? Back : CloseRounded;

  return (
    <SmallModal
      open={!!category}
      onClose={onClose}
      hideCloseButton
      width="500px">
      {category && (
        <Stack p={4} gap={5}>
          <Button
            onClick={onClose}
            variant="light"
            sx={{
              minWidth: 0,
              width: 'fit-content',
              padding: 0.25,
            }}>
            <CloseIcon
              sx={{
                width: '24px',
                height: '24px',
              }}
            />
          </Button>
          <Typography variant="h1" component="p">
            {ONBOARDING_CATEGORY_NAMES[category]}
          </Typography>

          <Progress progress={progress} includePercentage />

          <Stack
            gap={0}
            divider={
              <Divider
                variant="fullWidth"
                component="div"
                sx={{
                  borderColor: theme.palette.lightGrey.main,
                }}
              />
            }>
            {steps.map(step => (
              <StepRow
                key={step.id}
                category={category}
                setCategory={setCategory}
                step={step}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </SmallModal>
  );
}

export default OnboardingCategoryModal;
